import React, { useState, useEffect, useCallback } from 'react';
import { X, Share, Plus, Download, Clock, Camera, Smartphone, FolderOpen, MoreVertical, Check, Monitor } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

// ── localStorage keys (unified) ──
const LS_INSTALLED = 'rektra_pwa_installed';
const LS_NEVER = 'rektra_pwa_never';
const LS_REMIND_AFTER = 'rektra_pwa_remind_after';
const REMIND_DAYS = 3;

// ── Legacy key cleanup ──
const LEGACY_KEYS = ['rektra_pwa_dismissed', 'pwa_prompt_dismissed_at', 'pwa_installed'];

function cleanLegacyKeys() {
  LEGACY_KEYS.forEach(k => {
    try { localStorage.removeItem(k); } catch {}
  });
}

// ── Detect platform + browser ──
function detectPlatform() {
  if (typeof navigator === 'undefined') return { device: 'desktop', browser: 'chrome' };
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = (/iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && !window.MSStream;
  const isMac = /macintosh|mac os x/.test(ua) && navigator.maxTouchPoints <= 1;
  const isAndroid = /android/.test(ua);
  
  // Browser detection
  const isSafari = /safari/.test(ua) && !/chrome/.test(ua) && !/chromium/.test(ua);
  const isEdge = /edg\//.test(ua);
  const isFirefox = /firefox/.test(ua);
  const isChrome = /chrome/.test(ua) && !isEdge;
  
  let browser = 'chrome';
  if (isSafari) browser = 'safari';
  else if (isEdge) browser = 'edge';
  else if (isFirefox) browser = 'firefox';
  
  let device = 'desktop';
  if (isIOS) device = 'iphone';
  else if (isAndroid) device = 'android';
  else if (isMac) device = 'mac';
  
  return { device, browser };
}

// ── Standalone check ──
function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

// ── Capture beforeinstallprompt for Android/Desktop native install ──
let deferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}

/**
 * Unified PWA install flow.
 * - Inline dashboard card (not modal)
 * - Activity-based trigger: show after 2+ of {project, hours, receipt, job}
 * - Three dismiss options: install, remind later, never
 * - Device-specific step-by-step instructions
 *
 * Props:
 *   counts: { projects, time_entries, receipts, jobs }
 */
const PWAInstallFlow = ({ counts }) => {
  const { t } = useI18n();
  const [state, setState] = useState('hidden'); // hidden | prompt | steps | success
  const [platform] = useState(detectPlatform);

  useEffect(() => {
    cleanLegacyKeys();

    // Already installed
    if (isStandalone()) {
      try { localStorage.setItem(LS_INSTALLED, 'true'); } catch {}
      return;
    }
    if (localStorage.getItem(LS_INSTALLED) === 'true') return;

    // Permanently dismissed
    if (localStorage.getItem(LS_NEVER) === 'true') return;

    // Temporarily dismissed (remind later)
    const remindAfter = localStorage.getItem(LS_REMIND_AFTER);
    if (remindAfter && Date.now() < parseInt(remindAfter, 10)) return;

    // Activity trigger: at least 2 of 4 actions
    const c = counts || {};
    const activities = [
      (c.projects || 0) > 0,
      (c.time_entries || 0) > 0,
      (c.receipts || 0) > 0,
      (c.jobs || 0) > 0,
    ];
    const activityCount = activities.filter(Boolean).length;
    if (activityCount < 2) return;

    setState('prompt');
  }, [counts]);

  // Track install event
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = (e) => {
      if (e.matches) {
        try { localStorage.setItem(LS_INSTALLED, 'true'); } catch {}
        setState('success');
        setTimeout(() => setState('hidden'), 4000);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    // Android/Desktop Chrome/Edge: try native prompt first
    if (deferredPrompt && (platform.device === 'android' || platform.device === 'desktop' || platform.device === 'mac')) {
      try {
        deferredPrompt.prompt();
        const result = await deferredPrompt.userChoice;
        if (result.outcome === 'accepted') {
          localStorage.setItem(LS_INSTALLED, 'true');
          setState('success');
          setTimeout(() => setState('hidden'), 4000);
          deferredPrompt = null;
          return;
        }
      } catch {}
      deferredPrompt = null;
    }
    // Show manual steps
    setState('steps');
  }, [platform]);

  const handleRemindLater = useCallback(() => {
    const remindAt = Date.now() + REMIND_DAYS * 24 * 60 * 60 * 1000;
    try { localStorage.setItem(LS_REMIND_AFTER, String(remindAt)); } catch {}
    setState('hidden');
  }, []);

  const handleNever = useCallback(() => {
    try { localStorage.setItem(LS_NEVER, 'true'); } catch {}
    setState('hidden');
  }, []);

  const handleStepsDone = useCallback(() => {
    // After viewing steps, set remind later so we check back
    const remindAt = Date.now() + REMIND_DAYS * 24 * 60 * 60 * 1000;
    try { localStorage.setItem(LS_REMIND_AFTER, String(remindAt)); } catch {}
    setState('hidden');
  }, []);

  if (state === 'hidden') return null;

  // ── Success state ──
  if (state === 'success') {
    return (
      <section
        className="card p-5 border border-green-500/30 bg-green-500/5"
        data-testid="pwa-install-success"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/15 flex items-center justify-center">
            <Check size={20} className="text-green-400" />
          </div>
          <p className="text-sm font-medium">{t('pwa_install_success')}</p>
        </div>
      </section>
    );
  }

  // ── Steps state (device-specific instructions) ──
  if (state === 'steps') {
    return (
      <section
        className="card p-5 border border-border"
        data-testid="pwa-install-steps"
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-sm">{t('pwa_steps_title')}</h3>
          <button
            onClick={handleStepsDone}
            className="p-1 text-foreground-muted hover:text-foreground rounded-md transition-colors"
            aria-label={t('close')}
            data-testid="pwa-steps-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* iPhone Safari */}
        {platform.device === 'iphone' && (
          <ol className="space-y-4" data-testid="pwa-steps-iphone">
            <StepItem
              num={1}
              icon={<Share size={15} className="text-accent" />}
              title={t('pwa_ios_step1')}
              desc={t('pwa_ios_step1_desc')}
            />
            <StepItem
              num={2}
              icon={<Plus size={15} className="text-accent" />}
              title={t('pwa_ios_step2')}
              desc={t('pwa_ios_step2_desc')}
            />
            <StepItem
              num={3}
              icon={<Check size={15} className="text-accent" />}
              title={t('pwa_ios_step3')}
              desc={t('pwa_ios_step3_desc')}
            />
          </ol>
        )}

        {/* Mac Safari */}
        {platform.device === 'mac' && platform.browser === 'safari' && (
          <ol className="space-y-4" data-testid="pwa-steps-mac-safari">
            <StepItem
              num={1}
              icon={<Monitor size={15} className="text-accent" />}
              title={t('pwa_mac_safari_step1')}
              desc={t('pwa_mac_safari_step1_desc')}
            />
            <StepItem
              num={2}
              icon={<Plus size={15} className="text-accent" />}
              title={t('pwa_mac_safari_step2')}
              desc={t('pwa_mac_safari_step2_desc')}
            />
            <StepItem
              num={3}
              icon={<Check size={15} className="text-accent" />}
              title={t('pwa_mac_safari_step3')}
              desc={t('pwa_mac_safari_step3_desc')}
            />
          </ol>
        )}

        {/* Mac/Desktop Chrome */}
        {((platform.device === 'mac' || platform.device === 'desktop') && platform.browser === 'chrome') && (
          <ol className="space-y-4" data-testid="pwa-steps-chrome">
            <StepItem
              num={1}
              icon={<Monitor size={15} className="text-accent" />}
              title={t('pwa_chrome_step1')}
              desc={t('pwa_chrome_step1_desc')}
            />
            <StepItem
              num={2}
              icon={<Download size={15} className="text-accent" />}
              title={t('pwa_chrome_step2')}
              desc={t('pwa_chrome_step2_desc')}
            />
          </ol>
        )}

        {/* Mac/Desktop Edge */}
        {((platform.device === 'mac' || platform.device === 'desktop') && platform.browser === 'edge') && (
          <ol className="space-y-4" data-testid="pwa-steps-edge">
            <StepItem
              num={1}
              icon={<Monitor size={15} className="text-accent" />}
              title={t('pwa_edge_step1')}
              desc={t('pwa_edge_step1_desc')}
            />
            <StepItem
              num={2}
              icon={<Download size={15} className="text-accent" />}
              title={t('pwa_edge_step2')}
              desc={t('pwa_edge_step2_desc')}
            />
          </ol>
        )}

        {/* Mac/Desktop Firefox or unknown */}
        {((platform.device === 'mac' || platform.device === 'desktop') && (platform.browser === 'firefox' || (!['safari', 'chrome', 'edge'].includes(platform.browser)))) && (
          <ol className="space-y-4" data-testid="pwa-steps-firefox">
            <StepItem
              num={1}
              icon={<Monitor size={15} className="text-accent" />}
              title={t('pwa_firefox_step1')}
              desc={t('pwa_firefox_step1_desc')}
            />
          </ol>
        )}

        {/* Android */}
        {platform.device === 'android' && (
          <ol className="space-y-4" data-testid="pwa-steps-android">
            <StepItem
              num={1}
              icon={<MoreVertical size={15} className="text-accent" />}
              title={t('pwa_android_step1')}
              desc={t('pwa_android_step1_desc')}
            />
            <StepItem
              num={2}
              icon={<Download size={15} className="text-accent" />}
              title={t('pwa_android_step2')}
              desc={t('pwa_android_step2_desc')}
            />
            <StepItem
              num={3}
              icon={<Check size={15} className="text-accent" />}
              title={t('pwa_android_step3')}
              desc={t('pwa_android_step3_desc')}
            />
          </ol>
        )}

        <button
          onClick={handleStepsDone}
          className="mt-4 btn btn-secondary w-full text-sm"
          data-testid="pwa-steps-done"
        >
          {t('pwa_steps_done')}
        </button>
      </section>
    );
  }

  // ── Prompt state (main card) ──
  return (
    <section
      className="card p-5 border border-border bg-card"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-background rounded-xl flex items-center justify-center border border-border">
            <img src="/logos/app-icon-512.png" alt="Rektra" className="w-6 h-6 object-contain rounded" />
          </div>
          <h3 className="font-semibold text-sm" data-testid="pwa-prompt-title">
            {t('pwa_prompt_title')}
          </h3>
        </div>
        <button
          onClick={handleNever}
          className="p-1 text-foreground-muted hover:text-foreground rounded-md transition-colors"
          aria-label={t('close')}
          data-testid="pwa-prompt-close"
        >
          <X size={16} />
        </button>
      </div>

      <p className="text-sm text-foreground-muted mb-4 leading-relaxed">
        {t('pwa_prompt_desc')}
      </p>

      {/* Benefits */}
      <div className="space-y-2.5 mb-4">
        <Benefit icon={<Clock size={15} />} text={t('pwa_benefit_hours')} />
        <Benefit icon={<Camera size={15} />} text={t('pwa_benefit_receipts')} />
        <Benefit icon={<Smartphone size={15} />} text={t('pwa_benefit_app')} />
        <Benefit icon={<FolderOpen size={15} />} text={t('pwa_benefit_overview')} />
      </div>

      {/* Trust sentence */}
      <p className="text-xs text-foreground-muted mb-5">
        {t('pwa_prompt_trust')}
      </p>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <button
          onClick={handleInstall}
          className="btn btn-primary w-full text-sm"
          data-testid="pwa-install-btn"
        >
          <Download size={16} />
          {t('pwa_btn_install')}
        </button>
        <div className="flex gap-2">
          <button
            onClick={handleRemindLater}
            className="btn btn-secondary flex-1 text-xs"
            data-testid="pwa-remind-btn"
          >
            {t('pwa_btn_later')}
          </button>
          <button
            onClick={handleNever}
            className="btn btn-secondary flex-1 text-xs text-foreground-muted"
            data-testid="pwa-never-btn"
          >
            {t('pwa_btn_never')}
          </button>
        </div>
      </div>
    </section>
  );
};

// ── Reusable sub-components ──

const StepItem = ({ num, icon, title, desc }) => (
  <li className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      {icon}
    </div>
    <div>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-foreground-muted mt-0.5">{desc}</p>
    </div>
  </li>
);

const Benefit = ({ icon, text }) => (
  <div className="flex items-center gap-2.5 text-sm">
    <span className="text-accent flex-shrink-0">{icon}</span>
    <span>{text}</span>
  </div>
);

export default PWAInstallFlow;
